import { resetViewAndFilter as browseResetViewAndFilter } from "#skyward_browse";
import { mobileAccess, processViewPartHtml, virtualDir } from "#skyward_global";
import { notifyResize } from "#skyward_window";
import { Browse, BrowseData } from "./types";

interface BrowseDataCacheObject
{
    timeoutHandle: number;
    monitorRequestAjaxHandle: JQuery.Promise<any>;
    resetHandles: () => boolean;
}

interface MonitorBrowsePerformanceResponse
{
    allowReset: boolean;
    browseViewId?: number;
    browseFilterId?: number;
    banner: string;
}

let browseTimeoutEnabled: boolean;
let browseTimeoutSeconds: number;
let browseTimeoutSecondsMobile: number;
const classNames =
{
    longLoadBanner: "js-browseLongLoadBanner",
    longLoadResetButton: "js-browseLongLoadResetButton",
    browseBody: "browseBody"
};

export function initValues(timeoutEnabled: boolean, timeoutSeconds: number, timeoutSecondsMobile: number)
{
    browseTimeoutEnabled = timeoutEnabled;
    browseTimeoutSeconds = timeoutSeconds;
    browseTimeoutSecondsMobile = timeoutSecondsMobile;
}

export function monitorRequest(browseRequest: JQuery.Promise<any>, browse: Browse, browseData: Record<string, unknown>, routeModule: string, routeObject: string, routeAction: string)
{
    const cachedBrowseHandles = dataCache(browseRequest, browse);

    cachedBrowseHandles.timeoutHandle = window.setTimeout(handleLongBrowseLoad, getTimeoutMiliseconds(), cachedBrowseHandles, browse, browseData, routeModule, routeObject, routeAction);

    browseRequest.always(function ()
    {
        if (cachedBrowseHandles.resetHandles())
        {
            removeBrowseBanner(browse);
        }
    });
}

const dataCache = (function ()
{
    const map = new WeakMap();

    return function (browseRequest: JQuery.Promise<any>, browse: Browse)
    {
        const browseData = map.get(browse) || {};
        const dataObject: BrowseDataCacheObject =
        {
            get timeoutHandle()
            {
                return browseData.timeoutHandle;
            },
            set timeoutHandle(value)
            {
                // track the last browseRequest
                browseData.browseRequest = browseRequest;
                resetHandles();
                browseData.timeoutHandle = value;
                map.set(browse, browseData);
            },
            get monitorRequestAjaxHandle()
            {
                return browseData.monitorRequestAjaxHandle;
            },
            set monitorRequestAjaxHandle(value)
            {
                browseData.monitorRequestAjaxHandle = value;
                map.set(browse, browseData);
            },
            resetHandles: resetHandles
        };

        return dataObject;

        function resetHandles()
        {
            // don't reset anything if this isn't the latest browse request
            if (browseData.browseRequest !== browseRequest)
            {
                return false;
            }
            if (browseData.timeoutHandle !== undefined)
            {
                window.clearTimeout(browseData.timeoutHandle);
            }
            if (browseData.monitorRequestAjaxHandle !== undefined)
            {
                browseData.monitorRequestAjaxHandle.abort();
            }
            return true;
        }
    };
})();

function removeBrowseBanner(browse: Browse)
{
    getBrowseBannerContainer(browse).find(`.${classNames.longLoadBanner}`).remove();
}

function getTimeoutMiliseconds()
{
    return (mobileAccess ? browseTimeoutSecondsMobile : browseTimeoutSeconds) * 1000;
}

function handleLongBrowseLoad(cachedBrowseHandles: BrowseDataCacheObject, browse: Browse, browseData: Record<string, unknown>, routeModule: string, routeObject: string, routeAction: string)
{
    const data = $.extend(
        {
            routeModule: routeModule,
            routeObject: routeObject,
            routeAction: routeAction
        }, browseData);

    cachedBrowseHandles.monitorRequestAjaxHandle = $.ajax(
        {
            url: `${virtualDir}SkySys/Browse/MonitorBrowsePerformance`,
            type: "POST",
            dataType: "JSON",
            data: data,
            activityIndicator: { hideGlass: true },
            success: async function (responseData: MonitorBrowsePerformanceResponse)
            {
                if (browseTimeoutEnabled && responseData.allowReset)
                {
                    await displayLongRunningBrowseBanner(browse, responseData);
                }
            }
        });
}

async function displayLongRunningBrowseBanner(browse: Browse, data: MonitorBrowsePerformanceResponse)
{
    const processedBanner = await processViewPartHtml(data.banner);
    const banner = $(processedBanner);
    if (banner.length === 0)
    {
        return;
    }

    //prevent multiple banners
    removeBrowseBanner(browse);

    banner.addClass(classNames.longLoadBanner);
    const container = getBrowseBannerContainer(browse);
    banner.find(`.${classNames.longLoadResetButton}`).one("click", { browse: browse, data: data }, resetViewAndFilter);
    container.prepend(banner);
    // trigger resize to reposition anchored modal layers if necessary
    notifyResize();
}

function getBrowseBannerContainer(browse: Browse)
{
    let container;
    if (browse.browse !== undefined)
    {
        container = browse.browse.find(`.${classNames.browseBody}`);
        if (container.length === 0)
        {
            container = browse.browse;
        }
    }
    else
    {
        container = $(`#${browse.id}`);
    }
    return container;
}

function resetViewAndFilter(event: JQuery.ClickEvent)
{
    const browse = event.data.browse as Browse;
    const data = event.data.data as BrowseData;
    const postData =
    {
        isLongLoad: true,
        routeModule: browse.pageModule || browse.routeModule,
        routeObject: browse.pageObject || browse.routeObject,
        browseScreen: browse.pageScreen,
        browseName: browse.browseName,
        taskName: browse.taskName,
        browseType: browse.browseType
    };
    browseResetViewAndFilter(data.browseViewId!, data.browseFilterId!, browse.id, postData);
}
